import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

export function Loader({ label = "Loading" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-[#55708F]" data-testid="loader">
      <Loader2 className="h-7 w-7 animate-spin text-[#FF6B00]" />
      <p className="mt-3 text-sm">{label}…</p>
    </div>
  );
}

export function SectionHeader({ eyebrow, title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mb-1">{eyebrow}</p>}
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#071126]">{title}</h2>
        {subtitle && <p className="text-[#071126]/60 mt-1 text-sm sm:text-base">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action, testid }) {
  return (
    <div className="pa-card p-10 text-center flex flex-col items-center" data-testid={testid || "empty-state"}>
      {Icon && (
        <span className="h-14 w-14 rounded-2xl bg-[#FF6B00]/10 grid place-items-center mb-4">
          <Icon className="h-7 w-7 text-[#FF6B00]" />
        </span>
      )}
      <h3 className="text-lg font-semibold text-[#071126]">{title}</h3>
      {hint && <p className="text-[#071126]/55 mt-1.5 max-w-sm text-sm">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

const PASSION_STYLE = {
  photography: "bg-[#55708F]/12 text-[#3f566f]",
  "fitness-wellness": "bg-[#8FAF9B]/20 text-[#4d7361]",
  "art-creative": "bg-[#FF6B00]/12 text-[#c25400]",
  music: "bg-[#55708F]/12 text-[#3f566f]",
  cooking: "bg-[#FF6B00]/12 text-[#c25400]",
  writing: "bg-[#8FAF9B]/20 text-[#4d7361]",
};
export const PASSION_LABEL = {
  photography: "Photography",
  "fitness-wellness": "Fitness & Wellness",
  "art-creative": "Art & Creative",
  music: "Music",
  cooking: "Cooking",
  writing: "Writing",
};
export const ALL_PASSIONS = ["photography", "fitness-wellness", "art-creative", "music", "cooking", "writing"];

export function PassionBadge({ slug, className = "" }) {
  const style = PASSION_STYLE[slug] || "bg-[#FFC94A]/25 text-[#071126]";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${style} ${className}`}>
      {PASSION_LABEL[slug] || slug}
    </span>
  );
}

export function Chip({ active, children, ...props }) {
  return (
    <button
      {...props}
      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all pa-focus ${
        active
          ? "bg-[#071126] text-white"
          : "bg-white border border-[#071126]/10 text-[#071126]/70 hover:border-[#FF6B00]/40 hover:text-[#071126]"
      }`}
    >
      {children}
    </button>
  );
}

export function PrimaryButton({ as: As = "button", className = "", children, ...props }) {
  const cls = `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B00] text-white font-semibold text-sm shadow-[0_6px_18px_rgba(255,107,0,0.28)] hover:bg-[#e05e00] active:scale-[0.98] transition-all pa-focus disabled:opacity-60 disabled:cursor-not-allowed ${className}`;
  if (As === Link) return <Link className={cls} {...props}>{children}</Link>;
  return <As className={cls} {...props}>{children}</As>;
}

export function GhostButton({ as: As = "button", className = "", children, ...props }) {
  const cls = `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#071126]/12 text-[#071126] font-semibold text-sm hover:border-[#071126]/30 active:scale-[0.98] transition-all pa-focus ${className}`;
  if (As === Link) return <Link className={cls} {...props}>{children}</Link>;
  return <As className={cls} {...props}>{children}</As>;
}

export function money(price, currency = "USD") {
  if (!price || price === 0) return "Free";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(price);
}

export function niceDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
}
